import { useState, ChangeEvent, FormEvent, DragEvent, useEffect } from 'react';
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { setupAPIclient } from '../../services/setupAPIclient';
import LocalStorage from '../../helpers/localstorage';
import Select, { components, OptionProps, MultiValueGenericProps, StylesConfig } from "react-select";
import LayoutSubmasterToUser from '../../components/layoutConfig/layoutSubmasterToUser';

interface User {
  id: number;
  email: string;
  name: string;
  number_phone: string;
  can_view: boolean;
  typeofcontract: string; // "Contratado" | "Contratada"
}

interface CompanyData {
  company_id: number;
  name_company: string;
  region_id: number;
  region_name: string;
  classification: string;
  unit_id: number;
  unit_name: string;
  location_id: number;
  location_name: string;
}

interface Signatory {
  user_id: number;
}

/**
 * Option type usado no react-select
 */
type UserOption = {
  value: number;
  label: string; // usado internamente, mas renderizamos custom
  name: string;
  email: string;
  typeofcontract: string;
};

function UploadPageSubmaster() {
  const isDarkTheme =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "dark";

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [signatories, setSignatories] = useState<Signatory[]>([]);

  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);

  const [classification, setClassification] = useState<string>("");
  const [sms, setSms] = useState(false);
  const [whats, setWhats] = useState(false);
  const [emailNoti, setEmailNoti] = useState(true);
  const [reminderDays, setReminderDays] = useState<number>(7);
  const [dueDate, setDueDate] = useState<string>("");

  const [contractName, setContractName] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthTokenUserToSubmaster());
  const ApiClient = setupAPIclient(token);

  // Buscar notificação existente (mantive a função como estava)
  const handleGetNotification = async () => {
    await ApiClient.post("/management/get/notification");
  };

  // Upload de arquivo (drag & drop + file input)
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

  // Buscar usuários (com typeofcontract vindo da API)
  const handleGetUsers = async () => {
    try {
      const response = await ApiClient.post("/management/get/users");
      if (response.data.success) {
        // garanta que typeofcontract existe (padroniza ucfirst)
        const mapped: User[] = response.data.success.map((u: any) => ({
          id: u.id,
          email: u.email,
          name: u.name,
          number_phone: u.number_phone,
          can_view: u.can_view ?? false,
          typeofcontract: typeof u.typeofcontract === "string"
            ? (u.typeofcontract.charAt(0).toUpperCase() + u.typeofcontract.slice(1).toLowerCase())
            : ""
        }));
        setUsers(mapped);
      }
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      toast.error("Erro ao carregar usuários");
    }
  };

  // Buscar empresas
  const handleGetCompanies = async () => {
    try {
      const response = await ApiClient.post("/management/get/company");
      if (response.data.success) setCompanies(response.data.success);
    } catch (err) {
      console.error("Erro ao buscar empresas:", err);
      toast.error("Erro ao carregar empresas");
    }
  };

  // Atualiza região, unidade e local ao selecionar empresa
  const handleCompanyChange = (companyId: number) => {
    setSelectedCompany(companyId);
    const company = companies.find(c => c.company_id === companyId);
    if (company) {
      setSelectedRegion(company.region_id);
      setSelectedUnit(company.unit_id);
      setSelectedLocation(company.location_id);
      // se quiser, poderia pré-selecionar classification da company (comentei)
      // setClassification(company.classification || "");
    } else {
      setSelectedRegion(null);
      setSelectedUnit(null);
      setSelectedLocation(null);
    }
  };

  // Submissão (mantive validações / FormData)
  const handleSubmitUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return toast.error("Selecione um documento!");
    if (!dueDate) return toast.error("Informe a data de vencimento!");
    if (!contractName.trim()) return toast.error("Informe o nome do contrato!");
    if (!classification) return toast.error("Selecione a classificação do contrato!");
    if (signatories.length === 0) return toast.error("Adicione pelo menos um signatário!");

    try {
      const dataForm = new FormData();
      dataForm.append("uploaddoc", selectedFile);
      dataForm.append("data_vencimento", dueDate);
      dataForm.append("sms", String(sms));
      dataForm.append("whatts", String(whats));
      dataForm.append("email", String(emailNoti));
      dataForm.append("message", message);
      dataForm.append("reminder_days", String(reminderDays));
      dataForm.append("name", contractName);
      dataForm.append("classification", classification);

      // Signatários (array)
      dataForm.append("user_id", JSON.stringify(signatories));

      if (selectedCompany) dataForm.append("company_id", String(selectedCompany));
      if (selectedRegion) dataForm.append("region_id", String(selectedRegion));
      if (selectedUnit) dataForm.append("unit_id", String(selectedUnit));
      if (selectedLocation) dataForm.append("location_id", String(selectedLocation));

      const response = await ApiClient.post("/management/uploaddoc", dataForm, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data?.success) {
        toast.success("✅ Documento enviado com sucesso!");
        // limpa estados
        setSelectedFile(null);
        setPreviewUrl(null);
        setDueDate("");
        setSignatories([]);
        setSelectedCompany(null);
        setSelectedRegion(null);
        setSelectedUnit(null);
        setSelectedLocation(null);
        setSms(false);
        setWhats(false);
        setEmailNoti(true);
        setReminderDays(7);
        setContractName("");
        setMessage("");
        setClassification("");
      } else {
        const msg = response.data?.message || "Erro no envio do documento";
        toast.error(`❌ ${msg}`);
      }
    } catch (err: any) {
      console.error("Erro ao enviar:", err);
      const serverMessage = err?.response?.data?.message;
      if (serverMessage) toast.error(`❌ ${serverMessage}`);
      else toast.error("❌ Erro no envio do documento");
    }
  };

  useEffect(() => {
    handleGetUsers();
    handleGetCompanies();
  }, []);

  // Configuração das opções para react-select (usado no Select multi)
  const userOptions: UserOption[] = users.map(u => ({
    value: u.id,
    label: `${u.name} (${u.email})`,
    name: u.name,
    email: u.email,
    typeofcontract: u.typeofcontract || ""
  }));

  // custom render para Option (duas linhas, com destaque para typeofcontract)
  const CustomOption = (props: OptionProps<UserOption, true>) => {
    const { data, innerRef, innerProps, isFocused, isSelected } = props;
    const contractType = data.typeofcontract || "";
    const badgeStyle: React.CSSProperties = {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: "16px",
      marginTop: 6
    };
    // cores por tipo
    const color = contractType.toLowerCase().includes("contratada")
      ? "#8e44ad" // roxo
      : "#2980b9"; // azul para 'Contratado' ou default

    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          padding: "8px 12px",
          background: isDarkTheme
            ? (isFocused ? "#111827" : "#1F2937")
            : (isFocused ? "#f5f7fb" : "white"),
          color: isDarkTheme ? "#F9FAFB" : "#0F172A",
          display: "flex",
          flexDirection: "column",
          borderLeft: isSelected ? `4px solid ${color}` : undefined
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {data.name}{" "}
          <span style={{ fontWeight: 400, color: isDarkTheme ? "#cbd5e1" : "#666" }}>
            ({data.email})
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ ...badgeStyle, background: `${color}20`, color }}>{contractType}</div>
          <div style={{ fontSize: 12, color: isDarkTheme ? "#9CA3AF" : "#777" }}>{/* espaço para info adicional se desejar */}</div>
        </div>
      </div>
    );
  };

  // custom MultiValue (aparece acima quando selecionado — podemos mostrar só o nome e um badge pequeno)
  const CustomMultiValue = (props: MultiValueGenericProps<UserOption>) => {
    const contractType = props.data.typeofcontract || "";
    const badgeStyle: React.CSSProperties = {
      marginLeft: 6,
      padding: "1px 6px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      lineHeight: "14px",
      color: "#fff",
      background: contractType.toLowerCase().includes("contratada") ? "#8e44ad" : "#2980b9"
    };
    return (
      <components.MultiValue {...props}>
        <span style={{ fontWeight: 600, color: isDarkTheme ? "#e5e7eb" : "#1f2937" }}>{props.data.name}</span>
        <span style={badgeStyle}>{contractType}</span>
      </components.MultiValue>
    );
  };

  // estilos do react-select para ficar mais compacto
  const selectStyles: StylesConfig<UserOption, true> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 44,
      backgroundColor: isDarkTheme ? "#111827" : "#fff",
      borderColor: isDarkTheme ? "#374151" : provided.borderColor,
      boxShadow: state.isFocused
        ? `0 0 0 1px ${isDarkTheme ? "rgb(93, 173, 226)" : "#5dade2"}`
        : "none",
      ":hover": {
        borderColor: isDarkTheme ? "rgb(93, 173, 226)" : "#5dade2",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkTheme ? "#1F2937" : "#fff",
      border: isDarkTheme ? "1px solid #374151" : provided.border,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: isDarkTheme ? "#1F2937" : "#fff",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkTheme ? "#F9FAFB" : provided.color,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkTheme ? "#9CA3AF" : provided.color,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkTheme ? "#F9FAFB" : provided.color,
    }),
    valueContainer: (provided) => ({ ...provided, padding: "4px 8px" }),
    multiValue: (provided) => ({
      ...provided,
      background: isDarkTheme ? "#0F172A" : "#eef5ff",
      border: isDarkTheme ? "1px solid #374151" : "none",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDarkTheme ? "#F9FAFB" : "#0f172a",
    }),
    option: (provided) => ({ ...provided, padding: 0 })
  };

  // classification options
  const classificationOptions = [
    "Locação",
    "Prestação de Serviço",
    "Compra e Venda",
    "Parceria / Colaboração",
    "Fornecimento",
    "Trabalho / RH",
    "Licenciamento / Tecnologia"
  ];

  // botão desabilitado até subir o documento
  const isSubmitDisabled = !selectedFile;

  return (
    <LayoutSubmasterToUser>
      <div className="row py-4 upload-contract-page">
        <form onSubmit={handleSubmitUpload}>
          <div className="row g-4">

            {/* Sidebar esquerda */}
            <div className="col-md-4 d-flex">
              <div className="card shadow-sm border-0 rounded-3 w-100 h-100">
                <div className="card-body">

                  {/* Nome do contrato */}
                  <div className="card p-3 shadow-lg border-0 rounded-4 mb-4">
                    <h5 className="fw-bold mb-2">
                      <i className="bi bi-file-text me-2" style={{ color: "#5dade2" }}></i>
                      <span style={{ color: "#5dade2" }}> Nome do contrato</span>
                    </h5>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Digite o nome do contrato"
                      value={contractName}
                      onChange={e => setContractName(e.target.value)}
                    />
                  </div>

                  {/* Classificação (manual) */}
                  <h5 className="fw-bold mb-2 mt-3" style={{ color: "#5dade2" }}>
                    <i className="bi bi-diagram-3 me-2"></i> Classificação
                  </h5>
                  <select
                    className="form-select mb-3"
                    value={classification}
                    onChange={e => setClassification(e.target.value)}
                  >
                    <option value="">-- Selecione a classificação --</option>
                    {classificationOptions.map((c, idx) => (
                      <option key={idx} value={c}>{c}</option>
                    ))}
                  </select>

                  {/* Usuários (react-select com typeofcontract destacado) */}
                  <h5 className="fw-bold mb-2" style={{ color: "#5dade2" }}>
                    <i className="bi bi-people me-2"></i> Signatários
                  </h5>
                  <Select
                    options={userOptions}
                    isMulti
                    placeholder="Selecione um ou mais usuários..."
                    value={signatories.map(s => userOptions.find(u => u.value === s.user_id))}
                    onChange={(selected) => {
                      const selectedArray = Array.isArray(selected) ? selected : [];
                      setSignatories(selectedArray.map(u => ({ user_id: u.value })));
                    }}
                    className="mb-3"
                    components={{ Option: CustomOption as any, MultiValue: CustomMultiValue as any }}
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />

                  {/* Empresa / Região / Unidade / Local */}
                  <h5 className="fw-bold mb-2" style={{ color: "#5dade2" }}>
                    <i className="bi bi-building me-2"></i> Empresa / Região / Unidade
                  </h5>
                  <select
                    className="form-select mb-2"
                    value={selectedCompany ?? ""}
                    onChange={e => handleCompanyChange(Number(e.target.value))}
                  >
                    <option value="">-- Selecione a empresa --</option>
                    {companies.map(c => (
                      <option key={c.company_id} value={c.company_id}>
                        {c.name_company}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-select mb-2"
                    value={selectedRegion ?? ""}
                    onChange={e => setSelectedRegion(Number(e.target.value))}
                  >
                    <option value="">-- Selecione a região (opcional) --</option>
                    {companies
                      .filter(c => c.company_id === selectedCompany)
                      .map(c => (
                        <option key={c.region_id} value={c.region_id}>
                          {c.region_name}
                        </option>
                      ))}
                  </select>

                  <select
                    className="form-select mb-2"
                    value={selectedUnit ?? ""}
                    onChange={e => setSelectedUnit(Number(e.target.value))}
                  >
                    <option value="">-- Selecione a unidade (opcional) --</option>
                    {companies
                      .filter(c => c.company_id === selectedCompany)
                      .map(c => (
                        <option key={c.unit_id} value={c.unit_id}>
                          {c.unit_name}
                        </option>
                      ))}
                  </select>

                  {/* Vencimento */}
                  <h5 className="fw-bold mb-2" style={{ color: "#5dade2" }}>
                    <i className="bi bi-calendar-event me-2" style={{ color: "#5dade2" }}></i> Vencimento
                  </h5>
                  <input
                    type="date"
                    className="form-control mb-4"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                  />

                  {/* Reminder days */}
                  <h6 className="fw-bold mb-2" style={{ color: isDarkTheme ? "#cbd5e1" : "#6b7280" }}>Lembrete (dias)</h6>
                  <input
                    type="number"
                    className="form-control mb-4"
                    min={1}
                    value={reminderDays}
                    onChange={e => setReminderDays(Number(e.target.value))}
                  />

                  {/* Mensagem da notificação <h6 className="fw-bold text-secondary mb-2">Mensagem da notificação</h6>
                  <textarea
                    className="form-control mb-4"
                    placeholder="Digite a mensagem da notificação"
                    rows={3}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />*/}
                  

                  {/* Botão Enviar — desabilitado até ter documento */}
                  <button
                    type="submit"
                    className="btn w-100 py-2"
                                        style={{background:"#5dade2", color:"white"}}

                    disabled={isSubmitDisabled}
                    aria-disabled={isSubmitDisabled}
                  >
                    <i className="bi bi-upload me-2" style={{ color: "#fff" }}></i> Enviar documento
                  </button>

                  {/* Aviso no rodapé */}
                  {isSubmitDisabled ? (
                    <p className="text-danger text-center mt-3 fw-bold">
                      ⚠ Coloque o documento primeiro para seguir.
                    </p>
                  ) : (
                    <p className="text-success text-center mt-3 fw-bold">
                      ✅ Documento carregado — pronto para enviar.
                    </p>
                  )}

                </div>
              </div>
            </div>

            {/* Área de upload */}
            <div className="col-md-8 d-flex">
              <div
                className="card shadow-sm border-0 rounded-3 w-100 h-100 d-flex align-items-center justify-content-center upload-dropzone"
                style={{ minHeight: "600px" }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {!previewUrl ? (
                  <div className="text-center p-5" style={{ color: isDarkTheme ? "#cbd5e1" : "#6b7280" }}>
                    <i className="bi bi-file-earmark-arrow-up display-1 mb-3" style={{ color: isDarkTheme ? "#F9FAFB" : "#6b7280" }}></i>
                    <p className="fw-bold" style={{ color: isDarkTheme ? "#F9FAFB" : "#334155" }}>Arraste o documento aqui</p>
                    <p style={{ color: isDarkTheme ? "#9CA3AF" : "#64748b" }}>ou clique no botão abaixo</p>
                    <label className="btn"                       style={{background:"#5dade2", color:"white"}}
>
                      <i className="bi bi-folder2-open me-2" style={{ color: "#fff" }}></i> Selecionar arquivo
                      <input type="file" hidden onChange={handleFileChange} />
                    </label>
                  </div>
                ) : (
                  <iframe
                    src={previewUrl}
                    title="Preview do contrato"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      borderRadius: "8px"
                    }}
                  />
                )}
              </div>
            </div>

          </div>
        </form>
      </div>
    </LayoutSubmasterToUser>
  );
}

export default UploadPageSubmaster;
